// src/ui/steps/step1-rol.jsx: omschrijf opdracht + AI rol-suggestie
import React from "react";
import { rankItem, rankings } from "@tanstack/match-sorter-utils";
import {
  FW, FwButton, FwTextarea, FwSelect, FwInlineBanner,
  StepHeader, NiveauBadge, AsideCard, RolTooltip,
} from "../primitives";
import { aiHelp } from "../../lib/ai";
import { buildFestivalFamilies, stripVariantLetter } from "../../lib/festival-families";
import { rollenFilmAv, rollenFestival, bulletsVoorRol, omschrijvingVoorRol } from "../../data/rollen";

// Stam-match: vangt morfologische varianten die match-sorter's subsequence-
// regel mist (bv. "stuntman" → "Stuntpersoon", "production" → "Productie").
// Een query-token matcht een woord uit het doel als ze een gemeenschappelijke
// prefix van ≥ 5 tekens delen én die ≥ 55% van het kortste woord beslaat.
// Tokens korter dan 5 kwalificeren nooit, zodat korte termen geen ruis geven.
const stripDiacritics = (s) =>
  (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const gedeeldePrefixLengte = (a, b) => {
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  return i;
};
const stamRank = (target, token) => {
  const t = stripDiacritics(token);
  if (t.length < 5) return 0;
  let best = 0;
  for (const woord of stripDiacritics(target).split(/[^a-z0-9]+/).filter(Boolean)) {
    const p = gedeeldePrefixLengte(woord, t);
    if (p >= 5 && p >= 0.55 * Math.min(woord.length, t.length)) best = Math.max(best, p);
  }
  return best;
};

export const StepRol = ({ state, setState, next, back, disableAI = false, titleOverride, subOverride }) => {
  const huidige = state.rollen?.[0];
  const [omschrijving, setOmschrijving] = React.useState(state.omschrijving || "");
  React.useEffect(() => {
    setState(s => s.omschrijving === omschrijving ? s : { ...s, omschrijving });
  }, [omschrijving]);
  // AI-voorstellen leven in globale state zodat ze ook bij terug-navigatie
  // (en page-reload via localStorage) zichtbaar blijven zolang de omschrijving
  // niet wezenlijk is veranderd.
  const [suggesties, setSuggestiesLocal] = React.useState(
    state.aiSuggesties?.omschrijving === (state.omschrijving || "")
      ? state.aiSuggesties?.data || null
      : null,
  );
  const setSuggesties = (data) => {
    setSuggestiesLocal(data);
    setState(s => ({
      ...s,
      aiSuggesties: data ? { omschrijving: s.omschrijving || "", data } : null,
    }));
  };
  const [loading, setLoading] = React.useState(false);
  const [aiError, setAiError] = React.useState(false);
  const [selectedRol, setSelectedRol] = React.useState(huidige?.rol || null);
  const [selectedDep, setSelectedDep] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [searchFocused, setSearchFocused] = React.useState(false);
  const zoekInputRef = React.useRef(null);
  const rolLijstRef = React.useRef(null);
  // Reset scroll naar top als de zoekterm verandert of als het departement-filter
  // wisselt: anders kun je halverwege de lijst zoeken en de match niet zien.
  React.useEffect(() => {
    if (rolLijstRef.current) rolLijstRef.current.scrollTop = 0;
  }, [searchQuery, selectedDep]);

  const isFestival = state.type === "festival";
  // Festival: dedupliceer variant-rijen tot families (variant A t/m E volgt in
  // step2 via de variant-keuze of criteria-modal). Film/AV blijft 1 rij per rol.
  const rollen = React.useMemo(
    () => isFestival ? buildFestivalFamilies(rollenFestival) : rollenFilmAv,
    [isFestival],
  );

  const aiCtrlRef = React.useRef(null);
  React.useEffect(() => () => aiCtrlRef.current?.abort(), []);

  const vraagAI = async () => {
    if (!omschrijving.trim() || omschrijving.trim().length < 10) return;
    aiCtrlRef.current?.abort();
    const ctrl = new AbortController();
    aiCtrlRef.current = ctrl;
    setLoading(true);
    setSuggesties(null);
    setAiError(false);
    // Minimale zichtbare laad-duur: bij een cache-hit lost de call direct op,
    // anders ziet de gebruiker de spinner niet en lijkt er niets te gebeuren.
    const [res] = await Promise.all([
      aiHelp.suggestRol(omschrijving, isFestival, { signal: ctrl.signal }),
      new Promise(r => setTimeout(r, 300)),
    ]);
    if (ctrl.signal.aborted) return;
    setLoading(false);
    if (res) setSuggesties(res);
    else setAiError(true);
  };

  // De Rollengids/het Functiehandboek is de bron van waarheid. Een AI-suggestie
  // mag alleen bepalen wélke rol het is: afdeling en zwaarteniveau komen altijd
  // uit het echte rol-record, nooit uit (mogelijk verzonnen) AI-velden. Zoek
  // daarom op rolnaam (+ afdeling als die klopt), met fallback op alleen rolnaam.
  // Festival: strip eerst de variant-letter zodat we de familie vinden.
  const resolveCanoniek = (rolObj) => {
    const naam = isFestival ? stripVariantLetter(rolObj.rol) : rolObj.rol;
    return rollen.find(x => x.rol === naam && x.dep === rolObj.dep)
        || rollen.find(x => x.rol === naam)
        || null;
  };

  const verderMet = (rolObj) => {
    const canoniek = resolveCanoniek(rolObj);
    if (!canoniek) return;  // AI verzon een rol die niet bestaat → niet doorgaan
    const familieRec = isFestival ? canoniek : null;

    const bestaande = state.rollen?.[0];
    const rolNaamNw = canoniek.rol;
    const depNw = canoniek.dep;
    const wisseltRol = bestaande && bestaande.rol !== rolNaamNw;
    // Timesheet (uren) hangt aan het departement: bij rolwissel binnen dezelfde
    // afdeling (bv. Lead-editor → Assembly-editor) past de timesheet nog en
    // behouden we 'm; bij een ander departement wissen we 'm (sjabloon verschilt).
    const wisUren = wisseltRol && bestaande.dep !== depNw;
    const nieuwId = window.crypto?.randomUUID?.() || `r-${Date.now()}`;

    // Niveau bepalen:
    //  - festival, familie met varianten: null (wordt in step2 via variant-keuze geset)
    //  - festival, familie zonder varianten: vaste schaal uit variant_schalen._
    //  - film/AV: niveaus uit het canonieke rol-record
    let niveausLijst, gekozenNiveau;
    if (isFestival && familieRec) {
      niveausLijst = familieRec.niveaus;
      gekozenNiveau = familieRec.heeftVarianten
        ? null
        : familieRec.variant_schalen._;
    } else {
      // film/AV: niveaus komen uit het canonieke record, niet uit de AI-suggestie.
      niveausLijst = Array.isArray(canoniek.niveaus) && canoniek.niveaus.length
        ? canoniek.niveaus
        : (canoniek.niveau ? [canoniek.niveau] : []);
      gekozenNiveau = niveausLijst[0];
    }

    const nieuweRol = {
      id: wisseltRol ? nieuwId : (bestaande?.id || nieuwId),
      rol: rolNaamNw,
      dep: depNw,
      niveaus: niveausLijst,
      niveau: gekozenNiveau,
      niveau_bron: "rol-lookup",
      ...(isFestival && familieRec ? {
        familie: familieRec.rol,
        variant_schalen: familieRec.variant_schalen,
        heeftVarianten: familieRec.heeftVarianten,
        festivaltype: null,
        // Functiedoel uit het Functiehandboek Filmfestivals, getoond op de
        // variant-keuzepagina (step2) zodat de gekozen rol context heeft.
        functiedoel: familieRec.omschrijving || "",
      } : {}),
      uren: wisUren
        ? { hoofdwerk: 0, voorbereiding: 0, afronding: 0, pr_promotie: 0 }
        : (bestaande?.uren || { hoofdwerk: 0, voorbereiding: 0, afronding: 0, pr_promotie: 0 }),
      urenDetails: wisUren ? undefined : bestaande?.urenDetails,
      // Rol-wisseling invalideert alle downstream rol-specifieke antwoorden
      // (criteria-vragen 3.1, werkdagen 4, loondienst-blokken).
      criteriaAntwoorden: wisseltRol ? {} : (bestaande?.criteriaAntwoorden || {}),
      werkdagen:    wisseltRol ? undefined : bestaande?.werkdagen,
      dienstverband: wisseltRol ? undefined : bestaande?.dienstverband,
      periodiek:    wisseltRol ? undefined : bestaande?.periodiek,
    };
    setState(s => ({
      ...s,
      omschrijving,
      rollen: [nieuweRol],
    }));
    next();
  };

  const departementen = [...new Set(rollen.map(r => r.dep))].sort((a, b) => a.localeCompare(b, "nl"));
  // Voor Film/AV: bullets uit de Gids (kolom D); fallback naar de lange
  // omschrijving als bullets ontbreken. Festival: altijd de omschrijving uit
  // het Functiehandboek Filmfestivals (geen bullets-bron beschikbaar).
  const beschrijvingVoor = (dep, rolNaam) => {
    if (!isFestival) {
      const bullets = bulletsVoorRol(dep, rolNaam);
      if (bullets?.length) return { bullets, tekst: null };
    }
    const tekst = omschrijvingVoorRol(isFestival ? "festival" : "film_av", dep, rolNaam) || "";
    return { bullets: null, tekst };
  };

  // Ranking via @tanstack/match-sorter-utils: rol > alias > dep > omschrijving.
  // Multi-token query: alle tokens moeten in minstens één veld matchen; per veld
  // sommeren we de ranks zodat een rol die op meerdere tokens scoort hoger uitkomt.
  // Aliases (Synoniemenlijst, bron B / bron 6): als een match via alias komt, tonen
  // we welke alias het was zodat de gebruiker context heeft.
  const rankOpts = { keepDiacritics: false };
  // De omschrijving/bullets zijn lange vrije tekst. match-sorter's laagste tier
  // (MATCHES) matcht op losse letters-in-volgorde (subsequence), waardoor een
  // korte query als "Scenarist" als subsequence in willekeurige lange teksten
  // zit (bv. de omschrijving van Rigging Artist). Met threshold CONTAINS telt de
  // omschrijving alleen mee bij een echte deelstring-match of sterker, rolnaam,
  // alias en departement houden hun volledige fuzzy-tolerantie.
  const omsRankOpts = { keepDiacritics: false, threshold: rankings.CONTAINS };
  const gefilterdeRollen = React.useMemo(() => {
    const q = searchQuery.trim();
    const baseFilter = (r) => !selectedDep || r.dep === selectedDep;
    if (!q) {
      return rollen
        .filter(baseFilter)
        .slice()
        .sort((a, b) => a.rol.localeCompare(b.rol, "nl"))
        .map(r => ({ rol: r, matchedAlias: null }));
    }
    const tokens = q.split(/\s+/).filter(Boolean);

    return rollen
      .filter(baseFilter)
      .map(r => {
        // Zoek-haystack: voor Film/AV de bullets joined (uit de Gids col D),
        // voor festival de omschrijving uit het Functiehandboek.
        const bullets = !isFestival ? bulletsVoorRol(r.dep, r.rol) : null;
        const omschrijving = omschrijvingVoorRol(isFestival ? "festival" : "film_av", r.dep, r.rol);
        const oms = bullets?.length ? bullets.join(" ") : (omschrijving || "");
        const aliasArr = Array.isArray(r.aliases) ? r.aliases : [];
        // Per token: rank op rol/dep/oms + best-rankende alias (incl. index)
        const perToken = tokens.map(t => {
          let bestAlias = { rank: 0, passed: false };
          let bestAliasIdx = -1;
          for (let i = 0; i < aliasArr.length; i++) {
            const ar = rankItem(aliasArr[i], t, rankOpts);
            if (ar.passed && ar.rank > bestAlias.rank) {
              bestAlias = ar;
              bestAliasIdx = i;
            }
          }
          return {
            rol: rankItem(r.rol, t, rankOpts),
            alias: bestAlias,
            aliasIdx: bestAliasIdx,
            dep: rankItem(r.dep, t, rankOpts),
            oms: rankItem(oms, t, omsRankOpts),
            // Stam-fallback op de rolnaam: vangt varianten als stuntman →
            // Stuntpersoon. Laagste prioriteit, telt alleen mee als de andere
            // velden niet matchen.
            stem: stamRank(r.rol, t),
          };
        });
        const allPass = perToken.every(p => p.rol.passed || p.alias.passed || p.dep.passed || p.oms.passed || p.stem > 0);
        const sumRol = perToken.reduce((s, p) => s + p.rol.rank, 0);
        const sumAlias = perToken.reduce((s, p) => s + p.alias.rank, 0);
        const sumDep = perToken.reduce((s, p) => s + p.dep.rank, 0);
        const sumOms = perToken.reduce((s, p) => s + p.oms.rank, 0);
        const sumStem = perToken.reduce((s, p) => s + p.stem, 0);
        // Toon alias-indicator alleen als alias-match sterker is dan rol-naam-match
        // (bij gelijke score kiest de gebruiker zelf; dan is rol-naam zelf-evident).
        let matchedAlias = null;
        if (sumAlias > sumRol) {
          const score = new Map();
          for (const p of perToken) {
            if (p.aliasIdx >= 0) {
              score.set(p.aliasIdx, (score.get(p.aliasIdx) || 0) + p.alias.rank);
            }
          }
          let bestIdx = -1, bestScore = 0;
          for (const [idx, s] of score) {
            if (s > bestScore) { bestScore = s; bestIdx = idx; }
          }
          if (bestIdx >= 0) matchedAlias = aliasArr[bestIdx];
        }
        return { r, allPass, sumRol, sumAlias, sumDep, sumOms, sumStem, matchedAlias };
      })
      .filter(x => x.allPass)
      .sort((a, b) =>
        b.sumRol - a.sumRol ||
        b.sumAlias - a.sumAlias ||
        b.sumDep - a.sumDep ||
        b.sumOms - a.sumOms ||
        b.sumStem - a.sumStem,
      )
      .map(({ r, matchedAlias }) => ({ rol: r, matchedAlias }));
  }, [rollen, selectedDep, searchQuery]);

  const isLoondienst = isFestival && state.vorm === "loondienst";
  const isFestivalZzp = isFestival && state.vorm === "zzp";
  const isClient = !isFestival && state.perspectief === "opdrachtgever";
  const headerTitle = disableAI
    ? (isFestival ? "Kies je functie" : "Kies je rol")
    : isLoondienst
      ? "Vertel ons over je functie"
      : isFestivalZzp
        ? "Vertel ons over je opdracht"
        : isClient
          ? "Beschrijf de opdracht"
          : "Beschrijf je opdracht";
  const headerSub = disableAI
    ? (isFestival
        ? "Kies hieronder de juiste functie uit het Functiehandboek Filmfestivals."
        : "Kies hieronder de juiste rol uit de Rollengids.")
    : isLoondienst
      ? "We gebruiken je omschrijving om een functie voor te stellen. De cijfers staan voor de bijbehorende schaal in het salarishuis Filmfestivals. Daarna vul je het aantal werkdagen in. Zo kom je tot een onderbouwd voorstel voor je salarisonderhandeling."
      : isFestivalZzp
        ? "We gebruiken je omschrijving om een rol voor te stellen uit het Functiehandboek Referentiefuncties Filmfestivals. De cijfers staan voor de bijbehorende schaal uit de Richtlijn zzp-starttarieven Filmfestivals. Daarna vul je het geschatte aantal werkdagen in. Zo kom je tot een onderbouwd voorstel."
        : "We gebruiken je omschrijving om een rol voor te stellen uit het Opdracht Kompas en de Rollengids. In het voorstel zie je ook het bijbehorende zwaarteniveau.";

  return (
    <div>
      <StepHeader
        tag={isFestival ? "Filmfestival" : "Film/AV-productie"}
        vorm={isFestival ? state.vorm : undefined}
        perspectief={!isFestival ? state.perspectief : undefined}
        title={titleOverride ?? headerTitle}
        sub={subOverride ?? headerSub}
        onBack={back}
      />

      <div className="row-2col" style={{
        display: "grid",
        gridTemplateColumns: disableAI ? "1fr" : "1.3fr 1fr",
        gap: 24, alignItems: "start",
      }}>
        <div>
          {!disableAI && (
            <>
              <FwTextarea
                label={isLoondienst ? "Omschrijving van de functie" : "Omschrijving van de opdracht"}
                placeholder="Start met typen…"
                rows={6}
                value={omschrijving}
                onChange={e => setOmschrijving(e.target.value)}
                hint={`${omschrijving.length} tekens: meer detail = betere suggestie voor een ${isLoondienst ? "functie" : "rol"}`}
              />
              <div style={{ marginTop: 16, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
                <FwButton variant="primary" icon="ri-sparkling-2-line" arrow={false}
                  loading={loading}
                  onClick={vraagAI} disabled={loading || omschrijving.trim().length < 10}
                  style={{ whiteSpace: "nowrap" }}>
                  {loading ? "Even denken…" : (isLoondienst ? "Stel een functie voor" : "Stel een rol voor")}
                </FwButton>
                <FwButton
                  variant="secondary"
                  icon="ri-search-line"
                  arrow={false}
                  href="#rollengids"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("rollengids")?.scrollIntoView({ behavior: "smooth", block: "start" });
                    zoekInputRef.current?.focus({ preventScroll: true });
                  }}
                  style={{ whiteSpace: "nowrap" }}
                >
                  {isFestival ? "Zoek in het Functiehandboek Referentiefuncties" : "Zoek in de Rollengids"}
                </FwButton>
              </div>
            </>
          )}

          {!disableAI && aiError && (
            <FwInlineBanner type="warning" onDismiss={() => setAiError(false)} style={{ marginTop: 14 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Suggestie mislukt: kies je rol via de lijst hieronder.</div>
              {window.__lastAiError && (
                <div style={{ fontSize: 13, color: FW.navy2, fontFamily: "ui-monospace, monospace" }}>
                  {window.__lastAiError}
                </div>
              )}
            </FwInlineBanner>
          )}

          {!disableAI && suggesties && (
            <div style={{ marginTop: 24, padding: 20, background: "#FEF9B3", borderRadius: 16, border: `1.5px solid ${FW.yellow}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.navy }}>
                <i className="ri-sparkling-2-line" /> Voorstel op basis van je omschrijving
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                {suggesties.top_rollen?.map((r, i) => {
                  // De Rollengids is de bron van waarheid: resolve het echte
                  // rol-record en toon altijd de afdeling + niveaus daaruit.
                  // Een suggestie die niet matcht (AI verzon de rol) tonen we
                  // niet: anders zouden we verzonnen data presenteren.
                  const canoniek = resolveCanoniek(r);
                  if (!canoniek) return null;
                  const toonNaam = canoniek.rol;
                  const dep = canoniek.dep;
                  const niveausVoorRol = canoniek.niveaus || (canoniek.niveau ? [canoniek.niveau] : []);
                  const beschrijving = beschrijvingVoor(dep, toonNaam);
                  return (
                    <button key={i} onClick={() => verderMet(canoniek)} style={rolCard(i === 0)}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 18, fontWeight: 700, color: FW.navy }}>{toonNaam}</span>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                          {niveausVoorRol.map(n => <NiveauBadge key={n} niveau={n} />)}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, color: FW.gray500, marginTop: 4 }}>{dep}</div>
                      {beschrijving.bullets ? (
                        <ul style={{ fontSize: 14, color: FW.navy2, marginTop: 8, marginBottom: 0, paddingLeft: 20, lineHeight: 1.5 }}>
                          {beschrijving.bullets.map((b, bi) => (
                            <li key={bi} style={{ marginBottom: 4 }}>{b}</li>
                          ))}
                        </ul>
                      ) : beschrijving.tekst ? (
                        <div style={{ fontSize: 14, color: FW.navy2, marginTop: 8, lineHeight: 1.5 }}>
                          {beschrijving.tekst}
                        </div>
                      ) : null}
                      <div style={{ marginTop: 12, fontSize: 14, fontWeight: 700, color: FW.navy, display: "inline-flex", alignItems: "center", gap: 6 }}>
                        {isLoondienst ? "Kies deze functie" : "Kies deze rol"} <span style={{ display: "inline-grid", placeItems: "center", width: 22, height: 22, borderRadius: 999, background: FW.navy, color: "#fff", fontSize: 12 }}>→</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div id="rollengids" style={{ marginTop: 24, padding: 20, background: FW.cream, borderRadius: 16, border: `1.5px solid ${FW.gray100}`, scrollMarginTop: 24 }}>
            <div style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "stretch", flexWrap: "wrap" }}>
              <div style={{
                flex: "1 1 280px",
                display: "flex", alignItems: "center", gap: 8,
                background: FW.beige2,
                border: `1.5px solid ${searchFocused ? FW.navy : FW.taupe}`,
                borderRadius: 14, padding: "0 14px",
                minHeight: 48,
                boxShadow: searchFocused ? `0 0 0 3px rgba(46, 24, 71, 0.12)` : "none",
                transition: "border-color .15s ease, box-shadow .15s ease",
              }}>
                <i className="ri-search-line" style={{ fontSize: 18, color: FW.navy2, flexShrink: 0 }} />
                <input
                  ref={zoekInputRef}
                  placeholder={isFestival
                    ? "Of zoek hier op afdeling en rol of functie"
                    : "Zoek hier op rol of departement"}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                  style={{
                    flex: 1, minWidth: 0, border: 0, background: "transparent", padding: "12px 0",
                    fontFamily: "var(--font-sans)", fontSize: 15, color: FW.navy, outline: "none",
                  }}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    aria-label="Wis zoekterm"
                    title="Wis zoekterm"
                    style={{
                      display: "inline-grid", placeItems: "center",
                      width: 22, height: 22, borderRadius: 999,
                      background: FW.navy, color: "#fff",
                      border: 0, cursor: "pointer", flexShrink: 0,
                      fontSize: 12, lineHeight: 1, padding: 0,
                    }}
                  >
                    <i className="ri-close-line" style={{ fontSize: 14 }} />
                  </button>
                )}
              </div>
              <FwSelect
                value={selectedDep}
                onChange={setSelectedDep}
                options={departementen}
                placeholder={isFestival ? "Alle afdelingen" : "Alle departementen"}
                clearable
                ariaLabel="Filter op departement"
                style={{ flex: "0 1 240px", minWidth: 200 }}
              />
            </div>
              {/* Vaste titelbalk boven de lijst zodat duidelijk is dat de letters/cijfers
                  rechts het zwaarteniveau (film) resp. de schaal (festival) aangeven. */}
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "0 14px 8px", marginBottom: 2,
                fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.gray500,
              }}>
                <span>{isLoondienst ? "Functies" : "Rollen"}</span>
                <span>{isFestival ? "Schaal" : "Zwaarteniveau"}</span>
              </div>
              <div ref={rolLijstRef} style={{ maxHeight: 300, overflowY: "auto", display: "grid", gap: 6 }}>
                {gefilterdeRollen.map(({ rol: r, matchedAlias }, i) => {
                  const niveausLijst = r.niveaus || (r.niveau ? [r.niveau] : []);
                  return (
                    <button key={i} onClick={() => verderMet(r)} style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "10px 14px", background: FW.white, border: `1px solid ${FW.gray100}`,
                      borderRadius: 10, cursor: "pointer", fontFamily: "var(--font-sans)",
                      textAlign: "left",
                    }}>
                      <div>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 15, fontWeight: 700, color: FW.navy }}>
                          {r.rol}
                          <RolTooltip
                            tekst={(!isFestival && bulletsVoorRol(r.dep, r.rol))
                              || r.omschrijving
                              || omschrijvingVoorRol(isFestival ? "festival" : "film_av", r.dep, r.rol)}
                          />
                        </div>
                        <div style={{ fontSize: 13, color: FW.gray500 }}>{r.dep}</div>
                        {matchedAlias && (
                          <div style={{ fontSize: 12, color: FW.gray500, fontStyle: "italic", marginTop: 2 }}>
                            <i className="ri-arrow-right-line" style={{ marginRight: 4 }} />
                            ook bekend als: {matchedAlias}
                          </div>
                        )}
                      </div>
                      <div style={{ display: "inline-flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        {niveausLijst.map(n => <NiveauBadge key={n} niveau={n} />)}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
        </div>

        {!disableAI && (
        <AsideCard>
          <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 10, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <i className="ri-lightbulb-line" /> Tip
          </div>
          {isFestival ? (
            <>
              <div style={{ fontSize: 15, lineHeight: 1.5, color: FW.navy2 }}>
                Omschrijf zo concreet mogelijk wat je doet: je <b>taken</b>, en <b>verantwoordelijkheden</b>, hoe <b>zelfstandig</b> je werkt en of je <b>anderen aanstuurt</b>. Ben je hoofd van een afdeling of directeur? Voeg dan ook toe: het <b>aantal festivaldagen en locaties</b>, het <b>budget</b>, de <b>bezoekersaantallen</b> en de <b>omvang van je team</b>.
              </div>
              <div style={{ marginTop: 16, padding: 14, background: FW.cream, borderRadius: 12, fontSize: 13, color: FW.navy2, lineHeight: 1.5 }}>
                <b>Voorbeeld:</b><br/>
                {isLoondienst
                  ? "\"Programmeur Filmfestival: stelt het filmprogramma samen door films te scouten en selecteren. Werkt samen met distributeurs, filmmakers en externe programmeurs. Maakt keuzes op basis van artistieke visie en festivalbeleid. Stemt af met marketing en productie over de presentatie van het programma.\""
                  : "\"Coördinator Productie Filmfestival: regelt de voorbereiding en uitvoering van activiteiten tijdens het festival. Zoekt locaties, regelt faciliteiten en techniek en bewaakt de planning en het budget. Stuurt productiemedewerkers en vrijwilligers aan.\""}
              </div>
            </>
          ) : isClient ? (
            <div style={{ fontSize: 15, lineHeight: 1.5, color: FW.navy2 }}>
              Noem <b>taken</b>, <b>werkzaamheden</b>, <b>type productie</b>, <b>complexiteit</b>, <b>draaidagen</b>, <b>locaties</b>, <b>planning</b>, <b>teamomvang</b>, etc.
            </div>
          ) : (
            <>
              <div style={{ fontSize: 15, lineHeight: 1.5, color: FW.navy2 }}>
                Noem in ieder geval <b>je taken en werkzaamheden</b> en het <b>type productie</b>. Vul ook aan of het een{" "}
                <b style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  grote of complexe productie
                  <RolTooltip
                    ariaLabel="Definitie grote/complexe audiovisuele productie"
                    tekst="Grote/complexe audiovisuele productie: Belangrijke factoren zijn: het type audiovisuele productie, het afbreukrisico (financieel/reputatie), het budget, wijze van financiering (nationaal/internationaal), locaties (binnen/buiten), draaidagen (aantal en verschillende (internationale) locaties), omvang van het team. (Bron: Opdracht Kompas, Begrippenlijst)"
                  />
                </b>{" "}
                is, als je dat weet.
              </div>
              <div style={{ marginTop: 16, padding: 14, background: FW.cream, borderRadius: 12, fontSize: 13, color: FW.navy2, lineHeight: 1.5 }}>
                <b>Voorbeeld:</b><br/>
                "Focus Puller: Stelt de scherpte van de lens in voor elk shot. Beheert en onderhoudt de camera-apparatuur. Stuurt het camerateam aan en werkt samen met andere departementen."
              </div>
            </>
          )}
        </AsideCard>
        )}
      </div>

      {back && (
        <div className="step-nav" style={{ marginTop: 32, display: "flex", justifyContent: "space-between" }}>
          <FwButton variant="ghost" arrow={false} onClick={back}>← Terug</FwButton>
        </div>
      )}
    </div>
  );
};

const rolCard = (primary) => ({
  background: FW.white, border: `1.5px solid ${primary ? FW.navy : FW.gray100}`,
  borderRadius: 14, padding: 16, textAlign: "left", cursor: "pointer",
  fontFamily: "var(--font-sans)", width: "100%",
  boxShadow: primary ? "0 4px 12px rgba(0,0,0,.08)" : "none",
});

